import { useState, useEffect, useCallback } from 'react';
import { hrAPI, employeeAPI } from '@/lib/api';
import PageHeader from '@/components/Layout/PageHeader';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import SearchableSelect from '@/components/SearchableSelect';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import {
  Users, MagnifyingGlass, MapPin, Buildings, IdentificationBadge,
  Phone, Envelope, Bank, Lock, LockOpen, Eye, Key, ArrowsClockwise,
  CaretLeft, CaretRight, Copy, PencilSimple, SignOut, Star,
  CheckCircle, WarningCircle, Clock, XCircle, ArrowCounterClockwise, Plus,
} from '@phosphor-icons/react';

const fmt = (n) => new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 }).format(n || 0);

function StatusBadge({ status }) {
  const colors = {
    Working: 'bg-green-100 text-green-700',
    Resigned: 'bg-red-100 text-red-700',
    Relieved: 'bg-gray-200 text-gray-700',
    Terminated: 'bg-red-100 text-red-700',
    'Notice Period': 'bg-amber-100 text-amber-700',
    'Exit Processing': 'bg-orange-100 text-orange-700',
  };
  return (
    <span className={`px-2 py-0.5 rounded text-xs font-medium ${colors[status] || 'bg-gray-100 text-gray-600'}`} data-testid="emp-status-badge">
      {status || 'Unknown'}
    </span>
  );
}

export default function EmployeeMasterPage() {
  const [mainTab, setMainTab] = useState('directory');

  return (
    <div className="space-y-6" data-testid="employee-master-page">
      <PageHeader title="Employee Management" subtitle="Directory, Exit Processing & Appraisals" />

      <Tabs value={mainTab} onValueChange={setMainTab}>
        <TabsList className="bg-white border border-gray-200 rounded-xl p-1" data-testid="emp-master-tabs">
          <TabsTrigger value="directory" className="rounded-lg data-[state=active]:bg-[#0B1B3D] data-[state=active]:text-white">Directory</TabsTrigger>
          <TabsTrigger value="exit-tracker" className="rounded-lg data-[state=active]:bg-[#0B1B3D] data-[state=active]:text-white">Exit Tracker</TabsTrigger>
          <TabsTrigger value="appraisals" className="rounded-lg data-[state=active]:bg-[#0B1B3D] data-[state=active]:text-white">Appraisals</TabsTrigger>
        </TabsList>
        <TabsContent value="directory"><DirectoryTab /></TabsContent>
        <TabsContent value="exit-tracker"><ExitTrackerTab /></TabsContent>
        <TabsContent value="appraisals"><AppraisalsTab /></TabsContent>
      </Tabs>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════
   DIRECTORY TAB
   ═══════════════════════════════════════════════════════════════════ */
function DirectoryTab() {
  const [employees, setEmployees] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [deptFilter, setDeptFilter] = useState('all');
  const [locationFilter, setLocationFilter] = useState('all');
  const [departments, setDepartments] = useState([]);
  const [deptMaster, setDeptMaster] = useState([]);
  const [allEmployees, setAllEmployees] = useState([]);
  const [locations, setLocations] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [drawerMode, setDrawerMode] = useState('view'); // view | edit | exit | appraisal
  const [resetDialog, setResetDialog] = useState(false);
  const [resetEmpNo, setResetEmpNo] = useState('');
  const [resetPassword, setResetPassword] = useState('hearclear@123');
  const [bulkResetDialog, setBulkResetDialog] = useState(false);
  const [addEmpDialog, setAddEmpDialog] = useState(false);
  const [newEmpForm, setNewEmpForm] = useState({
    employee_number: '', display_name: '', first_name: '', last_name: '',
    department: '', sub_department: '', designation: '', location: '', date_of_joining: '',
    reporting_to: '', work_email: '', phone: '', gender: '', employment_type: 'Permanent',
  });
  const [addingEmp, setAddingEmp] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 400);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    hrAPI.getFilters().then(res => {
      setDepartments(res.data.departments || []);
      setLocations(res.data.locations || []);
    }).catch(() => {});
    hrAPI.getDepartmentMaster().then(res => {
      setDeptMaster(res.data.departments || []);
    }).catch(() => {});
    hrAPI.getEmployees({ page_size: 500 }).then(res => {
      setAllEmployees(res.data.employees || []);
    }).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, page_size: 50 };
      if (debouncedSearch) params.search = debouncedSearch;
      if (statusFilter !== 'all') params.status = statusFilter;
      if (deptFilter !== 'all') params.department = deptFilter;
      if (locationFilter !== 'all') params.location = locationFilter;
      const res = await hrAPI.getEmployees(params);
      setEmployees(res.data.employees || []);
      setTotal(res.data.total || 0);
      setTotalPages(res.data.total_pages || 1);
    } catch { toast.error('Failed to load employees'); }
    finally { setLoading(false); }
  }, [page, debouncedSearch, statusFilter, deptFilter, locationFilter]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(1); }, [debouncedSearch, statusFilter, deptFilter, locationFilter]);

  const handleResetPassword = async () => {
    try {
      await employeeAPI.setPassword({ employee_number: resetEmpNo, password: resetPassword });
      toast.success(`Password reset for ${resetEmpNo}`);
      setResetDialog(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const handleBulkReset = async () => {
    try {
      const res = await employeeAPI.bulkSetPasswords('hearclear@123');
      toast.success(res.data.message || 'Bulk passwords set');
      setBulkResetDialog(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const handleAddEmployee = async () => {
    if (!newEmpForm.employee_number || !newEmpForm.display_name) {
      toast.error('Employee number and name are required'); return;
    }
    setAddingEmp(true);
    try {
      await hrAPI.createEmployee(newEmpForm);
      toast.success(`Employee ${newEmpForm.employee_number} created with default password hearclear@123`);
      setAddEmpDialog(false);
      setNewEmpForm({ employee_number: '', display_name: '', first_name: '', last_name: '', department: '', sub_department: '', designation: '', location: '', date_of_joining: '', reporting_to: '', work_email: '', phone: '', gender: '', employment_type: 'Permanent' });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to create employee'); }
    finally { setAddingEmp(false); }
  };

  const openResetDialog = (empNo) => { setResetEmpNo(empNo); setResetPassword('hearclear@123'); setResetDialog(true); };
  const openDrawer = (emp, mode) => { setSelectedEmployee(emp); setDrawerMode(mode); };

  const activeCount = employees.filter(e => e.employment_status === 'Working').length;

  return (
    <div className="space-y-4">
      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="stats-card" data-testid="stat-total-employees">
          <div className="flex items-center gap-2 mb-1"><Users size={16} className="text-[#0B1B3D]" /><p className="text-xs text-gray-500">Total</p></div>
          <p className="text-2xl font-bold">{total}</p>
        </div>
        <div className="stats-card" data-testid="stat-active-employees">
          <div className="flex items-center gap-2 mb-1"><IdentificationBadge size={16} className="text-green-600" /><p className="text-xs text-gray-500">Active</p></div>
          <p className="text-2xl font-bold text-green-600">{activeCount}</p>
        </div>
        <div className="stats-card" data-testid="stat-departments">
          <div className="flex items-center gap-2 mb-1"><Buildings size={16} className="text-blue-600" /><p className="text-xs text-gray-500">Depts</p></div>
          <p className="text-2xl font-bold text-blue-600">{departments.length}</p>
        </div>
        <div className="stats-card" data-testid="stat-locations">
          <div className="flex items-center gap-2 mb-1"><MapPin size={16} className="text-purple-600" /><p className="text-xs text-gray-500">Locations</p></div>
          <p className="text-2xl font-bold text-purple-600">{locations.length}</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <Input placeholder="Search name, ID, or email..." value={search} onChange={e => setSearch(e.target.value)}
            className="pl-9 rounded-xl" data-testid="employee-search-input" />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-36 rounded-xl" data-testid="filter-status"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="Working">Working</SelectItem>
            <SelectItem value="Notice Period">Notice Period</SelectItem>
            <SelectItem value="Relieved">Relieved</SelectItem>
            <SelectItem value="Resigned">Resigned</SelectItem>
            <SelectItem value="Terminated">Terminated</SelectItem>
          </SelectContent>
        </Select>
        <SearchableSelect value={deptFilter} onChange={setDeptFilter} placeholder="Department" testid="filter-department" buttonClassName="w-44"
                  options={[{ value: 'all', label: 'All Departments' }, ...departments.map(d => ({ value: d, label: d }))]} />
        <SearchableSelect value={locationFilter || 'all'} onChange={setLocationFilter}
          options={[{ value: 'all', label: 'All Locations' }, ...locations.map(loc => ({ value: loc, label: loc }))]}
          placeholder="Location" testid="filter-location" buttonClassName="w-44 rounded-xl" />
        <Button variant="outline" size="sm" onClick={load} className="rounded-xl"><ArrowsClockwise size={14} className="mr-1" /> Refresh</Button>
        <Button variant="outline" size="sm" onClick={() => setBulkResetDialog(true)} className="rounded-xl"><Key size={14} className="mr-1" /> Bulk Set Passwords</Button>
        <Button size="sm" onClick={() => setAddEmpDialog(true)} className="rounded-xl bg-[#0B1B3D] hover:bg-[#16274D] text-white" data-testid="add-employee-btn"><Plus size={14} className="mr-1" /> Add Employee</Button>
      </div>

      {/* Table */}
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="employee-directory-table">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left font-semibold text-[#0B1B3D]">Employee</th>
                <th className="px-4 py-3 text-left font-semibold text-[#0B1B3D]">Department</th>
                <th className="px-4 py-3 text-left font-semibold text-[#0B1B3D]">Location</th>
                <th className="px-4 py-3 text-left font-semibold text-[#0B1B3D]">Contact</th>
                <th className="px-4 py-3 text-center font-semibold text-[#0B1B3D]">Status</th>
                <th className="px-4 py-3 text-left font-semibold text-[#0B1B3D]">Exit Date</th>
                <th className="px-4 py-3 text-center font-semibold text-[#0B1B3D]">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={7} className="text-center py-12 text-gray-400">Loading...</td></tr>
              ) : employees.length === 0 ? (
                <tr><td colSpan={7} className="text-center py-12 text-gray-400">No employees found</td></tr>
              ) : employees.map(emp => (
                <tr key={emp.employee_number} className="border-t border-gray-100 hover:bg-gray-50/50 cursor-pointer"
                  onClick={() => openDrawer(emp, 'view')} data-testid={`emp-row-${emp.employee_number}`}>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-[#0B1B3D] text-white flex items-center justify-center text-xs font-bold shrink-0">
                        {(emp.display_name || 'E')[0]}
                      </div>
                      <div>
                        <p className="font-medium text-[#0B1B3D]">{emp.display_name || emp.full_name}</p>
                        <p className="text-xs text-gray-500">{emp.employee_number} — {emp.job_title || '—'}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm">{emp.department || '—'}</td>
                  <td className="px-4 py-3 text-sm">{emp.location || '—'}</td>
                  <td className="px-4 py-3">
                    {emp.mobile_phone && <p className="text-xs text-gray-500 flex items-center gap-1"><Phone size={11} />{emp.mobile_phone}</p>}
                    {emp.work_email && <p className="text-xs text-gray-500 truncate max-w-[160px]">{emp.work_email}</p>}
                  </td>
                  <td className="px-4 py-3 text-center"><StatusBadge status={emp.employment_status} /></td>
                  <td className="px-4 py-3 text-sm text-gray-600" data-testid={`emp-exit-date-${emp.employee_number}`}>{emp.exit_date || '—'}</td>
                  <td className="px-4 py-3 text-center" onClick={ev => ev.stopPropagation()}>
                    <div className="flex gap-1 justify-center">
                      <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => openDrawer(emp, 'view')} data-testid={`view-emp-${emp.employee_number}`}>
                        <Eye size={14} className="text-gray-600" />
                      </Button>
                      <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => openDrawer(emp, 'edit')} data-testid={`edit-emp-${emp.employee_number}`}>
                        <PencilSimple size={14} className="text-blue-600" />
                      </Button>
                      <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => openResetDialog(emp.employee_number)}>
                        <Key size={14} className="text-amber-600" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-gray-500">Showing {((page - 1) * 50) + 1}–{Math.min(page * 50, total)} of {total}</p>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)} className="rounded-xl"><CaretLeft size={14} /></Button>
            <span className="text-sm text-gray-500 px-2">Page {page}/{totalPages}</span>
            <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)} className="rounded-xl"><CaretRight size={14} /></Button>
          </div>
        </div>
      )}

      {/* Employee Drawer */}
      <EmployeeDrawer employee={selectedEmployee} mode={drawerMode} onClose={() => setSelectedEmployee(null)}
        onPasswordReset={openResetDialog} onRefresh={load} setMode={setDrawerMode} locations={locations}
        deptMaster={deptMaster} allEmployees={allEmployees} />

      {/* Reset PW Dialog */}
      <Dialog open={resetDialog} onOpenChange={setResetDialog}>
        <DialogContent className="max-w-sm"><DialogHeader><DialogTitle>Reset Password</DialogTitle><DialogDescription>Set password for {resetEmpNo}</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <Input value={resetEmpNo} disabled />
            <Input value={resetPassword} onChange={e => setResetPassword(e.target.value)} placeholder="New password" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setResetDialog(false)}>Cancel</Button>
            <Button onClick={handleResetPassword} disabled={!resetPassword} data-testid="confirm-reset-btn">Reset</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Reset Dialog */}
      <Dialog open={bulkResetDialog} onOpenChange={setBulkResetDialog}>
        <DialogContent className="max-w-sm"><DialogHeader><DialogTitle>Bulk Set Passwords</DialogTitle>
          <DialogDescription>Set default password (hearclear@123) for all employees without a password.</DialogDescription></DialogHeader>
          <DialogFooter><Button variant="outline" onClick={() => setBulkResetDialog(false)}>Cancel</Button>
            <Button onClick={handleBulkReset}>Set Passwords</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Employee Dialog */}
      <Dialog open={addEmpDialog} onOpenChange={setAddEmpDialog}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="add-employee-dialog">
          <DialogHeader>
            <DialogTitle>Add New Employee</DialogTitle>
            <DialogDescription>Create a new employee record. Default password will be hearclear@123</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-xs font-medium text-gray-600 block mb-1">Employee Number *</label>
                <Input placeholder="e.g. GOI0100" value={newEmpForm.employee_number} onChange={e => setNewEmpForm(f => ({...f, employee_number: e.target.value.toUpperCase()}))} data-testid="new-emp-number" /></div>
              <div><label className="text-xs font-medium text-gray-600 block mb-1">Display Name *</label>
                <Input placeholder="Full name" value={newEmpForm.display_name} onChange={e => setNewEmpForm(f => ({...f, display_name: e.target.value}))} data-testid="new-emp-name" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-xs font-medium text-gray-600 block mb-1">First Name</label>
                <Input value={newEmpForm.first_name} onChange={e => setNewEmpForm(f => ({...f, first_name: e.target.value}))} /></div>
              <div><label className="text-xs font-medium text-gray-600 block mb-1">Last Name</label>
                <Input value={newEmpForm.last_name} onChange={e => setNewEmpForm(f => ({...f, last_name: e.target.value}))} /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-xs font-medium text-gray-600 block mb-1">Department</label>
                <SearchableSelect value={newEmpForm.department} onChange={v => setNewEmpForm(f => ({...f, department: v, sub_department: ''}))} placeholder="Select" testid="new-emp-department" buttonClassName="w-full"
                  options={[...deptMaster.map(d => ({ value: d.name, label: d.name }))]} /></div>
              <div><label className="text-xs font-medium text-gray-600 block mb-1">Sub Department</label>
                <Select value={newEmpForm.sub_department} onValueChange={v => setNewEmpForm(f => ({...f, sub_department: v}))}
                  disabled={!newEmpForm.department || !(deptMaster.find(d => d.name === newEmpForm.department)?.sub_departments?.length)}>
                  <SelectTrigger data-testid="new-emp-sub-department"><SelectValue placeholder={newEmpForm.department ? 'Select' : 'Pick dept first'} /></SelectTrigger>
                  <SelectContent>
                    {(deptMaster.find(d => d.name === newEmpForm.department)?.sub_departments || []).map(s => (
                      <SelectItem key={s} value={s}>{s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-xs font-medium text-gray-600 block mb-1">Designation</label>
                <Input placeholder="Job title" value={newEmpForm.designation} onChange={e => setNewEmpForm(f => ({...f, designation: e.target.value}))} /></div>
              <div><label className="text-xs font-medium text-gray-600 block mb-1">Reporting To</label>
                <Select value={newEmpForm.reporting_to} onValueChange={v => setNewEmpForm(f => ({...f, reporting_to: v}))}>
                  <SelectTrigger data-testid="new-emp-reporting-to"><SelectValue placeholder="Select manager" /></SelectTrigger>
                  <SelectContent className="max-h-60">
                    {allEmployees.filter(e => e.employee_number !== newEmpForm.employee_number).map(e => (
                      <SelectItem key={e.employee_number} value={e.employee_number}>
                        {e.display_name} ({e.employee_number})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-xs font-medium text-gray-600 block mb-1">Location</label>
                <SearchableSelect value={newEmpForm.location} onChange={v => setNewEmpForm(f => ({...f, location: v}))} placeholder="Select" testid="" buttonClassName="w-full"
                  options={[...locations.map(l => ({ value: l, label: l }))]} /></div>
              <div><label className="text-xs font-medium text-gray-600 block mb-1">Date of Joining</label>
                <Input type="date" value={newEmpForm.date_of_joining} onChange={e => setNewEmpForm(f => ({...f, date_of_joining: e.target.value}))} /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-xs font-medium text-gray-600 block mb-1">Email</label>
                <Input type="email" placeholder="Work email" value={newEmpForm.work_email} onChange={e => setNewEmpForm(f => ({...f, work_email: e.target.value}))} /></div>
              <div><label className="text-xs font-medium text-gray-600 block mb-1">Phone</label>
                <Input placeholder="Phone number" value={newEmpForm.phone} onChange={e => setNewEmpForm(f => ({...f, phone: e.target.value}))} /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-xs font-medium text-gray-600 block mb-1">Gender</label>
                <Select value={newEmpForm.gender} onValueChange={v => setNewEmpForm(f => ({...f, gender: v}))}>
                  <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Male">Male</SelectItem>
                    <SelectItem value="Female">Female</SelectItem>
                    <SelectItem value="Other">Other</SelectItem>
                  </SelectContent>
                </Select></div>
              <div><label className="text-xs font-medium text-gray-600 block mb-1">Employment Type</label>
                <Select value={newEmpForm.employment_type} onValueChange={v => setNewEmpForm(f => ({...f, employment_type: v}))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Permanent">Permanent</SelectItem>
                    <SelectItem value="Contract">Contract</SelectItem>
                    <SelectItem value="Intern">Intern</SelectItem>
                    <SelectItem value="Consultant">Consultant</SelectItem>
                  </SelectContent>
                </Select></div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddEmpDialog(false)}>Cancel</Button>
            <Button onClick={handleAddEmployee} disabled={addingEmp || !newEmpForm.employee_number || !newEmpForm.display_name}
              className="bg-[#0B1B3D] hover:bg-[#16274D]" data-testid="confirm-add-employee-btn">
              {addingEmp ? 'Creating...' : 'Create Employee'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════
   EMPLOYEE DRAWER — View / Edit / Exit / Appraisal
   ═══════════════════════════════════════════════════════════════════ */
function EmployeeDrawer({ employee, mode, onClose, onPasswordReset, onRefresh, setMode, locations, deptMaster, allEmployees }) {
  if (!employee) return null;
  return (
    <Dialog open={!!employee} onOpenChange={o => { if (!o) onClose(); }}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="employee-detail-drawer">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-[#0B1B3D] text-white flex items-center justify-center text-lg font-bold">
              {(employee.display_name || 'E')[0]}
            </div>
            <div>
              <span>{employee.display_name || employee.full_name}</span>
              <span className="text-sm font-normal text-gray-500 ml-2">({employee.employee_number})</span>
            </div>
          </DialogTitle>
          <DialogDescription>{employee.job_title} — {employee.department} — {employee.location}</DialogDescription>
        </DialogHeader>

        {/* Mode Tabs */}
        <div className="flex gap-2 mt-2 mb-4 border-b pb-3">
          <Button variant={mode === 'view' ? 'default' : 'outline'} size="sm" onClick={() => setMode('view')}
            className={`rounded-xl ${mode === 'view' ? 'bg-[#0B1B3D]' : ''}`} data-testid="drawer-view-btn">
            <Eye size={14} className="mr-1" /> View
          </Button>
          <Button variant={mode === 'edit' ? 'default' : 'outline'} size="sm" onClick={() => setMode('edit')}
            className={`rounded-xl ${mode === 'edit' ? 'bg-[#0B1B3D]' : ''}`} data-testid="drawer-edit-btn">
            <PencilSimple size={14} className="mr-1" /> Edit
          </Button>
          {employee.employment_status === 'Working' && (
            <Button variant={mode === 'exit' ? 'default' : 'outline'} size="sm" onClick={() => setMode('exit')}
              className={`rounded-xl ${mode === 'exit' ? 'bg-red-600 hover:bg-red-700' : 'text-red-600 border-red-200'}`} data-testid="drawer-exit-btn">
              <SignOut size={14} className="mr-1" /> Initiate Exit
            </Button>
          )}
          <Button variant={mode === 'appraisal' ? 'default' : 'outline'} size="sm" onClick={() => setMode('appraisal')}
            className={`rounded-xl ${mode === 'appraisal' ? 'bg-[#0B1B3D]' : ''}`} data-testid="drawer-appraisal-btn">
            <Star size={14} className="mr-1" /> Appraisal
          </Button>
        </div>

        {mode === 'view' && <ViewMode emp={employee} onPasswordReset={onPasswordReset} />}
        {mode === 'edit' && <EditMode emp={employee} onSaved={() => { onRefresh(); setMode('view'); }} locations={locations} deptMaster={deptMaster || []} allEmployees={allEmployees || []} />}
        {mode === 'exit' && <ExitMode emp={employee} onDone={() => { onRefresh(); onClose(); }} />}
        {mode === 'appraisal' && <AppraisalMode emp={employee} onDone={() => { onRefresh(); setMode('view'); }} />}
      </DialogContent>
    </Dialog>
  );
}

function ViewMode({ emp, onPasswordReset }) {
  const e = emp;
  const sections = [
    { title: 'Employment', fields: [
      ['Employee #', e.employee_number], ['Department', e.department], ['Sub Department', e.sub_department],
      ['Job Title', e.job_title], ['Location', e.location], ['Status', e.employment_status],
      ['Date Joined', e.date_joined], ['Reporting To', e.reporting_to], ['Band', e.band],
      ['Worker Type', e.worker_type], ['CTC (Annual)', e.ctc_annual ? `₹${fmt(e.ctc_annual)}` : '—'],
      ['Monthly Gross', e.monthly_gross ? `₹${fmt(e.monthly_gross)}` : '—'], ['Last Appraisal', e.last_appraisal_date || '—'],
    ]},
    { title: 'Contact', fields: [
      ['Work Email', e.work_email], ['Personal Email', e.personal_email], ['Mobile', e.mobile_phone],
    ]},
    { title: 'Bank & Compliance', fields: [
      ['Bank', e.bank_name], ['Account #', e.bank_account_number], ['IFSC', e.bank_ifsc],
      ['PAN', e.pan_number], ['Aadhaar', e.aadhaar_number], ['PF #', e.pf_number], ['UAN', e.uan_number],
    ]},
  ];

  return (
    <div className="space-y-5">
      {sections.map(s => (
        <div key={s.title}>
          <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">{s.title}</h4>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
            {s.fields.map(([label, value]) => (
              <div key={label} className="bg-gray-50 rounded-lg px-3 py-2">
                <p className="text-[10px] text-gray-400 uppercase">{label}</p>
                <p className="text-sm font-medium truncate">{value || '—'}</p>
              </div>
            ))}
          </div>
        </div>
      ))}

      {/* Exit Data Summary */}
      {e.exit_data && (
        <div>
          <h4 className="text-xs font-semibold text-red-500 uppercase tracking-wider mb-2">Exit Details</h4>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
            {[['Resignation Date', e.exit_data.resignation_date], ['Last Working Date', e.exit_data.last_working_date],
              ['Notice Period', `${e.exit_data.notice_period_days} days`], ['Status', e.exit_data.exit_status],
              ['Reason', e.exit_data.reason]].map(([label, value]) => (
              <div key={label} className="bg-red-50 rounded-lg px-3 py-2">
                <p className="text-[10px] text-red-400 uppercase">{label}</p>
                <p className="text-sm font-medium">{value || '—'}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="border-t pt-4 flex items-center justify-between">
        <div className="flex items-center gap-2">
          {e.has_password ? <><Lock size={16} className="text-green-600" /><span className="text-sm text-green-700">Password set</span></>
           : <><LockOpen size={16} className="text-amber-600" /><span className="text-sm text-amber-700">No password</span></>}
        </div>
        <Button variant="outline" size="sm" onClick={() => onPasswordReset(e.employee_number)} className="rounded-xl">
          <Key size={14} className="mr-1" /> Reset Password
        </Button>
      </div>
    </div>
  );
}

function EditField({ label, field, type = 'text', value, onChange }) {
  return (
    <div>
      <label className="text-xs text-gray-500 mb-1 block">{label}</label>
      <Input value={value} onChange={onChange} type={type} className="rounded-xl" data-testid={`edit-${field}`} />
    </div>
  );
}

function SetSalarySection({ emp }) {
  const [show, setShow] = useState(false);
  const [salary, setSalary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    basic: 0, hra: 0, special_allowance: 0, professional_allowance: 0,
    fuel_reimbursement: 0, travel_reimbursement: 0, food_coupons: 0,
    performance_incentives: 0, pf_employee: 0, pf_employer: 0,
    esi_employee: 0, esi_employer: 0, professional_tax: 0, gratuity: 0,
    monthly_gross: 0, ctc_annual: 0, effective_date: '',
  });

  const loadSalary = async () => {
    setLoading(true);
    try {
      const { data } = await hrAPI.getSalary(emp.employee_number);
      if (data.salary && Object.keys(data.salary).length > 0) {
        setSalary(data.salary);
        setForm(f => ({ ...f, ...data.salary }));
      }
    } catch {}
    finally { setLoading(false); }
  };

  const handleSave = async () => {
    if (!form.basic && !form.monthly_gross) { toast.error('Enter at least Basic or Monthly Gross'); return; }
    setSaving(true);
    try {
      const earnings = form.basic + form.hra + form.special_allowance + form.professional_allowance + form.fuel_reimbursement + form.travel_reimbursement + form.food_coupons + form.performance_incentives;
      const payload = { ...form, monthly_gross: form.monthly_gross || earnings, ctc_annual: form.ctc_annual || (form.monthly_gross || earnings) * 12 };
      await hrAPI.setSalary(emp.employee_number, payload);
      toast.success('Salary saved');
      setShow(false);
      loadSalary();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to save salary'); }
    finally { setSaving(false); }
  };

  const sf = (field, val) => setForm(f => ({ ...f, [field]: parseFloat(val) || 0 }));

  // Auto-fill salary components per HearClear compliance policy (April 2026):
  //   Basic = 50% of Gross
  //   HRA = 40% of Basic
  //   Special Allowance = Gross − Basic − HRA
  //   Employee PF = 12% of Basic   |   Employer PF = 12% of Basic
  //   Gratuity = 4.81% of Basic
  //   ESI = 0.75% (employee) / 3.25% (employer) if gross ≤ 21,000
  //   Professional Tax = state-based slab
  const autoFillFromCTC = (annualCTC) => {
    if (!annualCTC || annualCTC <= 0) return;
    const monthly = Math.round(annualCTC / 12);
    const basic = Math.round(monthly * 0.50);                  // 50% of Gross
    const hra = Math.round(basic * 0.40);                       // 40% of Basic
    const specialAllowance = Math.max(0, monthly - basic - hra); // Balance
    const pfEmployee = Math.round(basic * 0.12);                // 12% of Basic
    const pfEmployer = Math.round(basic * 0.12);                // 12% of Basic
    const gratuity = Math.round(basic * 0.0481);                // 4.81% of Basic
    const esiEmployee = monthly <= 21000 ? Math.round(monthly * 0.0075) : 0;
    const esiEmployer = monthly <= 21000 ? Math.round(monthly * 0.0325) : 0;
    const profTax = monthly > 20000 ? 200 : monthly > 15000 ? 150 : 0;

    setForm(f => ({
      ...f,
      ctc_annual: annualCTC,
      monthly_gross: monthly,
      basic, hra, special_allowance: specialAllowance,
      pf_employee: pfEmployee, pf_employer: pfEmployer,
      gratuity,
      esi_employee: esiEmployee, esi_employer: esiEmployer,
      professional_tax: profTax,
    }));
  };

  const autoFillFromMonthly = (monthlyGross) => {
    if (!monthlyGross || monthlyGross <= 0) return;
    autoFillFromCTC(monthlyGross * 12);
  };

  const fmtINR = (v) => v ? `INR ${Number(v).toLocaleString('en-IN')}` : 'INR 0';

  useEffect(() => { loadSalary(); }, []);

  if (loading) return <div className="text-center py-3 text-xs text-gray-400">Loading salary...</div>;

  return (
    <div data-testid="set-salary-section">
      {salary ? (
        <div className="bg-green-50 border border-green-200 rounded-xl p-3">
          <div className="flex items-center justify-between mb-2">
            <h4 className="text-xs font-semibold text-green-700 uppercase tracking-wider">Current Salary</h4>
            <Button size="sm" variant="outline" className="h-6 text-xs" onClick={() => { setForm(f => ({ ...f, ...salary })); setShow(!show); }} data-testid="edit-salary-btn">
              Edit
            </Button>
          </div>
          <div className="grid grid-cols-3 gap-2 text-sm">
            <div><span className="text-xs text-gray-500">Monthly Gross</span><p className="font-semibold">{fmtINR(salary.monthly_gross)}</p></div>
            <div><span className="text-xs text-gray-500">Annual CTC</span><p className="font-semibold">{fmtINR(salary.ctc_annual)}</p></div>
            <div><span className="text-xs text-gray-500">Basic</span><p className="font-semibold">{fmtINR(salary.basic)}</p></div>
          </div>
        </div>
      ) : (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 flex items-center justify-between">
          <span className="text-sm text-amber-700">No salary set for this employee</span>
          <Button size="sm" className="bg-[#0B1B3D] text-white text-xs h-7" onClick={() => setShow(!show)} data-testid="add-salary-btn">Add Salary</Button>
        </div>
      )}
      {show && (
        <div className="mt-3 border border-[#E2E8F0] rounded-xl p-4 space-y-3" data-testid="salary-form">
          <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Salary Breakup</h4>
          <div className="grid grid-cols-3 gap-3">
            {[
              ['Basic', 'basic'], ['HRA', 'hra'], ['Special Allowance', 'special_allowance'],
              ['Prof. Allowance', 'professional_allowance'], ['Fuel Reimb.', 'fuel_reimbursement'],
              ['Travel (LTA)', 'travel_reimbursement'], ['Food Coupons', 'food_coupons'],
              ['Perf. Incentives', 'performance_incentives'],
            ].map(([label, field]) => (
              <div key={field}>
                <label className="text-[10px] text-gray-500 block mb-0.5">{label}</label>
                <Input type="number" value={form[field] || ''} onChange={e => sf(field, e.target.value)} className="h-8 text-xs rounded-lg" data-testid={`salary-${field}`} />
              </div>
            ))}
          </div>
          <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider pt-2">Deductions</h4>
          <div className="grid grid-cols-3 gap-3">
            {[
              ['PF (Employee)', 'pf_employee'], ['PF (Employer)', 'pf_employer'],
              ['ESI (Employee)', 'esi_employee'], ['ESI (Employer)', 'esi_employer'],
              ['Professional Tax', 'professional_tax'], ['Gratuity', 'gratuity'],
            ].map(([label, field]) => (
              <div key={field}>
                <label className="text-[10px] text-gray-500 block mb-0.5">{label}</label>
                <Input type="number" value={form[field] || ''} onChange={e => sf(field, e.target.value)} className="h-8 text-xs rounded-lg" data-testid={`salary-${field}`} />
              </div>
            ))}
          </div>
          <div className="grid grid-cols-3 gap-3 pt-2 border-t">
            <div>
              <label className="text-[10px] text-gray-500 block mb-0.5">Monthly Gross</label>
              <Input type="number" value={form.monthly_gross || ''} onChange={e => { sf('monthly_gross', e.target.value); }} onBlur={e => { if (e.target.value && !form.basic) autoFillFromMonthly(parseFloat(e.target.value)); }} className="h-8 text-xs rounded-lg font-semibold" data-testid="salary-monthly-gross" />
            </div>
            <div>
              <label className="text-[10px] text-gray-500 block mb-0.5">Annual CTC</label>
              <Input type="number" value={form.ctc_annual || ''} onChange={e => { sf('ctc_annual', e.target.value); }} onBlur={e => { if (e.target.value) autoFillFromCTC(parseFloat(e.target.value)); }} className="h-8 text-xs rounded-lg font-semibold" data-testid="salary-ctc-annual" />
            </div>
            <div>
              <label className="text-[10px] text-gray-500 block mb-0.5">Effective Date</label>
              <Input type="date" value={form.effective_date || ''} onChange={e => setForm(f => ({ ...f, effective_date: e.target.value }))} className="h-8 text-xs rounded-lg" data-testid="salary-effective-date" />
            </div>
          </div>
          <div className="flex gap-2 pt-2">
            <Button size="sm" onClick={handleSave} disabled={saving} className="bg-[#0B1B3D] text-white text-xs h-7" data-testid="save-salary-btn">{saving ? 'Saving...' : 'Save Salary'}</Button>
            <Button size="sm" variant="outline" onClick={() => setShow(false)} className="text-xs h-7">Cancel</Button>
          </div>
        </div>
      )}
    </div>
  );
}

function EditMode({ emp, onSaved, locations, deptMaster = [], allEmployees = [] }) {
  const [form, setForm] = useState({
    display_name: emp.display_name || '', full_name: emp.full_name || '',
    job_title: emp.job_title || '', department: emp.department || '',
    sub_department: emp.sub_department || '', location: emp.location || '',
    reporting_to: emp.reporting_to || '', band: emp.band || '',
    worker_type: emp.worker_type || '', mobile_phone: emp.mobile_phone || '',
    work_email: emp.work_email || '', personal_email: emp.personal_email || '',
    bank_name: emp.bank_name || '', bank_account_number: emp.bank_account_number || '',
    bank_ifsc: emp.bank_ifsc || '', pan_number: emp.pan_number || '',
  });
  const [saving, setSaving] = useState(false);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSave = async () => {
    setSaving(true);
    try {
      const updates = {};
      Object.entries(form).forEach(([k, v]) => {
        const newVal = v === '__none__' ? '' : v;
        if (newVal !== (emp[k] || '') && (newVal !== '' || emp[k])) {
          updates[k] = newVal;
        }
      });
      if (Object.keys(updates).length === 0) { toast.info('No changes'); setSaving(false); return; }
      await hrAPI.updateEmployee(emp.employee_number, updates);
      toast.success('Employee updated');
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || 'Update failed'); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-5" data-testid="edit-mode">
      <div>
        <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Employment Details</h4>
        <div className="grid grid-cols-2 gap-3">
          <EditField label="Display Name" field="display_name" value={form.display_name} onChange={e => set('display_name', e.target.value)} />
          <EditField label="Full Name" field="full_name" value={form.full_name} onChange={e => set('full_name', e.target.value)} />
          <EditField label="Job Title" field="job_title" value={form.job_title} onChange={e => set('job_title', e.target.value)} />
          <div>
            <label className="text-xs text-gray-500 mb-1 block">Department</label>
            <SearchableSelect value={form.department} onChange={v => { set('department', v); set('sub_department', ''); }} placeholder="Select" testid="edit-department" buttonClassName="w-full"
                  options={[...deptMaster.map(d => ({ value: d.name, label: d.name }))]} />
          </div>
          <div>
            <label className="text-xs text-gray-500 mb-1 block">Sub Department</label>
            <Select value={form.sub_department} onValueChange={v => set('sub_department', v)}
              disabled={!form.department || !(deptMaster.find(d => d.name === form.department)?.sub_departments?.length)}>
              <SelectTrigger className="rounded-xl" data-testid="edit-sub-department"><SelectValue placeholder={form.department ? 'Select' : 'Pick dept first'} /></SelectTrigger>
              <SelectContent>
                {(deptMaster.find(d => d.name === form.department)?.sub_departments || []).map(s => (
                  <SelectItem key={s} value={s}>{s}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs text-gray-500 mb-1 block">Location</label>
            <Select value={form.location} onValueChange={v => set('location', v)}>
              <SelectTrigger className="rounded-xl" data-testid="edit-location"><SelectValue /></SelectTrigger>
              <SelectContent>{locations.map(loc => <SelectItem key={loc} value={loc}>{loc}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs text-gray-500 mb-1 block">Reporting To</label>
            <Select value={form.reporting_to} onValueChange={v => set('reporting_to', v)}>
              <SelectTrigger className="rounded-xl" data-testid="edit-reporting-to"><SelectValue placeholder="Select manager" /></SelectTrigger>
              <SelectContent className="max-h-60">
                <SelectItem value="__none__">— None —</SelectItem>
                {allEmployees.filter(e => e.employee_number !== emp.employee_number).map(e => (
                  <SelectItem key={e.employee_number} value={e.employee_number}>
                    {e.display_name} ({e.employee_number})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <EditField label="Band" field="band" value={form.band} onChange={e => set('band', e.target.value)} />
          <EditField label="Worker Type" field="worker_type" value={form.worker_type} onChange={e => set('worker_type', e.target.value)} />
        </div>
      </div>

      <SetSalarySection emp={emp} />

      <div>
        <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Contact</h4>
        <div className="grid grid-cols-2 gap-3">
          <EditField label="Mobile" field="mobile_phone" value={form.mobile_phone} onChange={e => set('mobile_phone', e.target.value)} />
          <EditField label="Work Email" field="work_email" value={form.work_email} onChange={e => set('work_email', e.target.value)} />
          <EditField label="Personal Email" field="personal_email" value={form.personal_email} onChange={e => set('personal_email', e.target.value)} />
        </div>
      </div>

      <div>
        <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Bank</h4>
        <div className="grid grid-cols-2 gap-3">
          <EditField label="Bank Name" field="bank_name" value={form.bank_name} onChange={e => set('bank_name', e.target.value)} />
          <EditField label="Account Number" field="bank_account_number" value={form.bank_account_number} onChange={e => set('bank_account_number', e.target.value)} />
          <EditField label="IFSC" field="bank_ifsc" value={form.bank_ifsc} onChange={e => set('bank_ifsc', e.target.value)} />
          <EditField label="PAN" field="pan_number" value={form.pan_number} onChange={e => set('pan_number', e.target.value)} />
        </div>
      </div>

      <div className="flex justify-end gap-2 pt-3 border-t">
        <Button variant="outline" onClick={onSaved} className="rounded-xl">Cancel</Button>
        <Button onClick={handleSave} disabled={saving} className="bg-[#0B1B3D] hover:bg-[#16274D] text-white rounded-xl" data-testid="save-employee-btn">
          {saving ? 'Saving...' : 'Save Changes'}
        </Button>
      </div>
    </div>
  );
}

function ExitMode({ emp, onDone }) {
  const today = new Date().toISOString().split('T')[0];
  const [form, setForm] = useState({ resignation_date: today, reason: '', last_working_date: '', notes: '' });
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  // Auto-calculate LWD
  const lwd = form.last_working_date || (() => {
    const d = new Date(form.resignation_date); d.setDate(d.getDate() + 30);
    return d.toISOString().split('T')[0];
  })();

  const handleSubmit = async () => {
    if (!form.resignation_date) return;
    setSubmitting(true);
    try {
      const { data } = await hrAPI.initiateExit(emp.employee_number, { ...form, last_working_date: lwd });
      setResult(data);
      toast.success('Exit initiated successfully');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setSubmitting(false); }
  };

  if (result) {
    const fnf = result.fnf_estimate || {};
    return (
      <div className="space-y-4" data-testid="exit-result">
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
          <h4 className="font-semibold text-amber-800 mb-2">Exit Initiated</h4>
          <div className="grid grid-cols-2 gap-2 text-sm">
            <div><span className="text-gray-500">Last Working Date:</span> <strong>{result.last_working_date}</strong></div>
            <div><span className="text-gray-500">Notice Period:</span> <strong>{result.notice_days} days</strong></div>
          </div>
        </div>
        <div>
          <h4 className="text-sm font-semibold text-[#0B1B3D] mb-2">Full & Final Estimate</h4>
          <div className="space-y-1 text-sm">
            <div className="flex justify-between"><span className="text-gray-500">Pending Salary</span><span>₹{fmt(fnf.pending_salary)}</span></div>
            <div className="flex justify-between"><span className="text-gray-500">Leave Encashment</span><span>₹{fmt(fnf.leave_encashment)}</span></div>
            <div className="flex justify-between"><span className="text-gray-500">Gratuity</span><span>₹{fmt(fnf.gratuity)}</span></div>
            <div className="flex justify-between font-semibold border-t pt-2 text-[#0B1B3D]"><span>Total Payable</span><span>₹{fmt(fnf.total_payable)}</span></div>
          </div>
        </div>
        <Button onClick={onDone} className="w-full bg-[#0B1B3D] text-white rounded-xl">Done</Button>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="exit-form">
      <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-sm text-red-700">
        Initiating exit for <strong>{emp.display_name}</strong> ({emp.employee_number}). This will change their status to "Notice Period" and calculate F&F settlement.
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-xs text-gray-500 mb-1 block">Resignation Date *</label>
          <Input type="date" value={form.resignation_date} onChange={e => setForm(f => ({ ...f, resignation_date: e.target.value }))}
            className="rounded-xl" data-testid="exit-resignation-date" />
        </div>
        <div>
          <label className="text-xs text-gray-500 mb-1 block">Last Working Date (30-day notice)</label>
          <Input type="date" value={lwd} onChange={e => setForm(f => ({ ...f, last_working_date: e.target.value }))}
            className="rounded-xl" data-testid="exit-lwd" />
        </div>
      </div>
      <div>
        <label className="text-xs text-gray-500 mb-1 block">Reason for Resignation</label>
        <textarea value={form.reason} onChange={e => setForm(f => ({ ...f, reason: e.target.value }))}
          className="w-full border rounded-xl p-3 text-sm resize-none" rows={2} placeholder="Personal, new opportunity, relocation..." data-testid="exit-reason" />
      </div>
      <div>
        <label className="text-xs text-gray-500 mb-1 block">Additional Notes</label>
        <Input value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} className="rounded-xl" placeholder="Optional" />
      </div>
      <Button onClick={handleSubmit} disabled={submitting} className="w-full bg-red-600 hover:bg-red-700 text-white rounded-xl h-11" data-testid="confirm-exit-btn">
        {submitting ? 'Processing...' : 'Confirm Exit Initiation'}
      </Button>
    </div>
  );
}

function AppraisalMode({ emp, onDone }) {
  const today = new Date().toISOString().split('T')[0];
  const [form, setForm] = useState({ appraisal_date: today, effective_date: today, rating: '', new_ctc_annual: '', new_monthly_gross: '', increment_percent: '', remarks: '' });
  const [history, setHistory] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    hrAPI.getAppraisals({ employee_number: emp.employee_number }).then(r => setHistory(r.data.appraisals || [])).catch(() => {});
  }, [emp.employee_number]);

  const handleSubmit = async () => {
    if (!form.appraisal_date || !form.effective_date) return;
    setSubmitting(true);
    try {
      const payload = { ...form };
      if (payload.new_ctc_annual) payload.new_ctc_annual = Number(payload.new_ctc_annual);
      if (payload.new_monthly_gross) payload.new_monthly_gross = Number(payload.new_monthly_gross);
      if (payload.increment_percent) payload.increment_percent = Number(payload.increment_percent);
      await hrAPI.recordAppraisal(emp.employee_number, payload);
      toast.success('Appraisal recorded & salary updated');
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setSubmitting(false); }
  };

  return (
    <div className="space-y-5" data-testid="appraisal-form">
      <div className="bg-gray-50 rounded-xl p-3 text-sm">
        <span className="text-gray-500">Last Appraisal:</span>{' '}
        <strong>{emp.last_appraisal_date || 'Never'}</strong>
        <span className="mx-2 text-gray-300">|</span>
        <span className="text-gray-500">Current CTC:</span>{' '}
        <strong>{emp.ctc_annual ? `₹${fmt(emp.ctc_annual)}` : '—'}</strong>
        <span className="mx-2 text-gray-300">|</span>
        <span className="text-gray-500">Monthly Gross:</span>{' '}
        <strong>{emp.monthly_gross ? `₹${fmt(emp.monthly_gross)}` : '—'}</strong>
      </div>

      <h4 className="text-sm font-semibold text-[#0B1B3D]">Record New Appraisal</h4>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-xs text-gray-500 mb-1 block">Appraisal Date *</label>
          <Input type="date" value={form.appraisal_date} onChange={e => setForm(f => ({ ...f, appraisal_date: e.target.value }))}
            className="rounded-xl" data-testid="appraisal-date" />
        </div>
        <div>
          <label className="text-xs text-gray-500 mb-1 block">Effective Date *</label>
          <Input type="date" value={form.effective_date} onChange={e => setForm(f => ({ ...f, effective_date: e.target.value }))}
            className="rounded-xl" data-testid="appraisal-effective-date" />
        </div>
        <div>
          <label className="text-xs text-gray-500 mb-1 block">Rating</label>
          <Select value={form.rating} onValueChange={v => setForm(f => ({ ...f, rating: v }))}>
            <SelectTrigger className="rounded-xl" data-testid="appraisal-rating"><SelectValue placeholder="Select" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="Exceeds Expectations">Exceeds Expectations</SelectItem>
              <SelectItem value="Meets Expectations">Meets Expectations</SelectItem>
              <SelectItem value="Needs Improvement">Needs Improvement</SelectItem>
              <SelectItem value="Below Expectations">Below Expectations</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="text-xs text-gray-500 mb-1 block">Increment %</label>
          <Input type="number" value={form.increment_percent} onChange={e => setForm(f => ({ ...f, increment_percent: e.target.value }))}
            className="rounded-xl" placeholder="e.g. 15" data-testid="appraisal-increment" />
        </div>
        <div>
          <label className="text-xs text-gray-500 mb-1 block">New CTC (Annual)</label>
          <Input type="number" value={form.new_ctc_annual} onChange={e => setForm(f => ({ ...f, new_ctc_annual: e.target.value }))}
            className="rounded-xl" placeholder="₹" data-testid="appraisal-new-ctc" />
        </div>
        <div>
          <label className="text-xs text-gray-500 mb-1 block">New Monthly Gross</label>
          <Input type="number" value={form.new_monthly_gross} onChange={e => setForm(f => ({ ...f, new_monthly_gross: e.target.value }))}
            className="rounded-xl" placeholder="₹" data-testid="appraisal-new-gross" />
        </div>
      </div>
      <div>
        <label className="text-xs text-gray-500 mb-1 block">Remarks</label>
        <textarea value={form.remarks} onChange={e => setForm(f => ({ ...f, remarks: e.target.value }))}
          className="w-full border rounded-xl p-3 text-sm resize-none" rows={2} placeholder="Appraisal notes..." data-testid="appraisal-remarks" />
      </div>
      <Button onClick={handleSubmit} disabled={submitting} className="w-full bg-[#0B1B3D] hover:bg-[#16274D] text-white rounded-xl h-11" data-testid="save-appraisal-btn">
        {submitting ? 'Saving...' : 'Record Appraisal & Update Salary'}
      </Button>

      {/* History */}
      {history.length > 0 && (
        <div>
          <h4 className="text-sm font-semibold text-[#0B1B3D] mb-2 mt-4">Appraisal History</h4>
          <div className="space-y-2">
            {history.map(a => (
              <div key={a.id} className="border rounded-xl p-3 text-sm">
                <div className="flex justify-between">
                  <span className="font-medium">{a.appraisal_date}</span>
                  <Badge variant="outline" className="text-xs">{a.rating || '—'}</Badge>
                </div>
                <div className="text-xs text-gray-500 mt-1">
                  CTC: ₹{fmt(a.old_ctc_annual)} → ₹{fmt(a.new_ctc_annual)}
                  {a.increment_percent > 0 && <span className="text-green-600 ml-2">+{a.increment_percent}%</span>}
                </div>
                {a.remarks && <p className="text-xs text-gray-400 mt-1">{a.remarks}</p>}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════
   EXIT TRACKER TAB
   ═══════════════════════════════════════════════════════════════════ */
function ExitTrackerTab() {
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editDialog, setEditDialog] = useState(null);
  const [editLWD, setEditLWD] = useState('');
  const [editReason, setEditReason] = useState('');

  const load = useCallback(async () => {
    try {
      const { data } = await hrAPI.getExitTracker();
      setEmployees(data.employees);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleComplete = async (empNo) => {
    if (!window.confirm(`Complete exit for ${empNo}? Status will change to Resigned.`)) return;
    try {
      await hrAPI.completeExit(empNo);
      toast.success('Exit completed');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const handleCancel = async (empNo, name) => {
    if (!window.confirm(`Cancel exit for ${name}? Employee will revert to Working status.`)) return;
    try {
      await hrAPI.cancelExit(empNo);
      toast.success('Exit cancelled — employee is now Working');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const openEdit = (emp) => {
    const ex = emp.exit_data || {};
    setEditLWD(ex.last_working_date || '');
    setEditReason(ex.reason || '');
    setEditDialog(emp);
  };

  const handleEditSave = async () => {
    try {
      await hrAPI.editExit(editDialog.employee_number, {
        last_working_date: editLWD,
        reason: editReason,
      });
      toast.success('Exit details updated & F&F recalculated');
      setEditDialog(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const noticePeriod = employees.filter(e => e.employment_status === 'Notice Period');
  const processing = employees.filter(e => e.employment_status === 'Exit Processing');
  const resigned = employees.filter(e => ['Resigned', 'Relieved'].includes(e.employment_status));
  const fmt = (n) => new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 }).format(n || 0);

  if (loading) return <div className="text-center py-12 text-gray-400">Loading...</div>;

  return (
    <div className="space-y-6" data-testid="exit-tracker-tab">
      <div className="grid grid-cols-3 gap-4">
        <Card className="border-amber-200 rounded-2xl"><CardContent className="p-4">
          <div className="flex items-center gap-2 mb-1"><Clock size={16} className="text-amber-600" /><span className="text-xs text-gray-500">Notice Period</span></div>
          <p className="text-2xl font-bold text-amber-600">{noticePeriod.length}</p>
        </CardContent></Card>
        <Card className="border-orange-200 rounded-2xl"><CardContent className="p-4">
          <div className="flex items-center gap-2 mb-1"><WarningCircle size={16} className="text-orange-600" /><span className="text-xs text-gray-500">Processing</span></div>
          <p className="text-2xl font-bold text-orange-600">{processing.length}</p>
        </CardContent></Card>
        <Card className="border-red-200 rounded-2xl"><CardContent className="p-4">
          <div className="flex items-center gap-2 mb-1"><XCircle size={16} className="text-red-600" /><span className="text-xs text-gray-500">Resigned / Relieved</span></div>
          <p className="text-2xl font-bold text-red-600">{resigned.length}</p>
        </CardContent></Card>
      </div>

      {employees.length === 0 ? (
        <p className="text-center text-gray-400 py-12">No employees in exit pipeline</p>
      ) : (
        <div className="space-y-3">
          {employees.map(emp => {
            const ex = emp.exit_data || {};
            const checklist = ex.checklist || {};
            const checkCount = Object.values(checklist).filter(Boolean).length;
            const totalChecks = Object.keys(checklist).length;
            const fnf = ex.fnf_estimate || {};
            return (
              <Card key={emp.employee_number} className="border rounded-2xl" data-testid={`exit-card-${emp.employee_number}`}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="font-semibold text-[#0B1B3D]">{emp.display_name || emp.full_name}</p>
                      <p className="text-xs text-gray-500">{emp.employee_number} — {emp.department} — {emp.location}</p>
                    </div>
                    <StatusBadge status={emp.employment_status} />
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3 text-sm">
                    <div><span className="text-xs text-gray-400">Resignation</span><p className="font-medium">{ex.resignation_date || emp.notice_date || '—'}</p></div>
                    <div><span className="text-xs text-gray-400">Exit Date</span><p className="font-medium">{ex.last_working_date || emp.exit_date || '—'}</p></div>
                    <div><span className="text-xs text-gray-400">Type</span><p className="font-medium truncate">{emp.exit_type || ex.exit_type || '—'}</p></div>
                    <div><span className="text-xs text-gray-400">Reason</span><p className="font-medium truncate">{emp.exit_reason || ex.reason || '—'}</p></div>
                  </div>

                  {/* F&F Estimate */}
                  {fnf.total_payable > 0 && (
                    <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-xl">
                      <p className="text-xs font-semibold text-blue-700 mb-2">Full & Final Settlement Estimate</p>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
                        <div><span className="text-xs text-gray-500">Pending Salary</span>
                          <p className="font-semibold">₹{fmt(fnf.pending_salary)}</p>
                          <p className="text-[10px] text-gray-400">{fnf.pending_days || 0} days</p>
                        </div>
                        <div><span className="text-xs text-gray-500">Leave Encashment</span>
                          <p className="font-semibold">₹{fmt(fnf.leave_encashment)}</p>
                          <p className="text-[10px] text-gray-400">{fnf.leave_days || 0} days</p>
                        </div>
                        <div><span className="text-xs text-gray-500">Gratuity</span><p className="font-semibold">₹{fmt(fnf.gratuity)}</p></div>
                        <div><span className="text-xs text-gray-500 font-bold">Total Payable</span><p className="text-lg font-bold text-blue-700">₹{fmt(fnf.total_payable)}</p></div>
                      </div>
                    </div>
                  )}

                  {totalChecks > 0 && (
                    <div className="mt-3">
                      <p className="text-xs text-gray-500 mb-1">Exit Checklist ({checkCount}/{totalChecks})</p>
                      <div className="flex flex-wrap gap-2">
                        {Object.entries(checklist).map(([k, v]) => (
                          <Badge key={k} variant="outline" className={`text-xs ${v ? 'bg-green-50 text-green-700 border-green-200' : 'text-gray-400'}`}>
                            {v ? <CheckCircle size={12} className="mr-1" /> : <XCircle size={12} className="mr-1" />}
                            {k.replace(/_/g, ' ')}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}

                  {emp.employment_status !== 'Resigned' && (
                    <div className="mt-3 pt-3 border-t flex justify-between flex-wrap gap-2">
                      <div className="flex gap-2">
                        <Button variant="outline" size="sm" onClick={() => handleCancel(emp.employee_number, emp.display_name || emp.employee_number)}
                          className="rounded-xl text-gray-600 border-gray-300" data-testid={`cancel-exit-${emp.employee_number}`}>
                          <ArrowCounterClockwise size={14} className="mr-1" /> Cancel Exit
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => openEdit(emp)}
                          className="rounded-xl text-blue-600 border-blue-200" data-testid={`edit-exit-${emp.employee_number}`}>
                          <PencilSimple size={14} className="mr-1" /> Edit LWD
                        </Button>
                      </div>
                      <Button variant="outline" size="sm" onClick={() => handleComplete(emp.employee_number)}
                        className="rounded-xl text-red-600 border-red-200" data-testid={`complete-exit-${emp.employee_number}`}>
                        <CheckCircle size={14} className="mr-1" /> Complete Exit
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Edit LWD Dialog */}
      {editDialog && (
        <Dialog open onOpenChange={(o) => { if (!o) setEditDialog(null); }}>
          <DialogContent className="max-w-md" data-testid="edit-exit-dialog">
            <DialogHeader>
              <DialogTitle>Edit Exit — {editDialog.display_name || editDialog.employee_number}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">Last Working Date</label>
                <Input type="date" value={editLWD} onChange={e => setEditLWD(e.target.value)} className="rounded-xl" data-testid="edit-lwd-input" />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">Reason</label>
                <Input value={editReason} onChange={e => setEditReason(e.target.value)} placeholder="Reason for resignation" className="rounded-xl" data-testid="edit-reason-input" />
              </div>
              <p className="text-xs text-gray-400">F&F will be recalculated based on the new LWD.</p>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditDialog(null)}>Cancel</Button>
              <Button onClick={handleEditSave} className="bg-[#0B1B3D] text-white rounded-xl" data-testid="save-exit-edit-btn">Save & Recalculate</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════
   APPRAISALS TAB
   ═══════════════════════════════════════════════════════════════════ */
function AppraisalsTab() {
  const [data, setData] = useState(null);
  const [allAppraisals, setAllAppraisals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('due'); // due | history

  useEffect(() => {
    Promise.all([
      hrAPI.getAppraisalsDue(),
      hrAPI.getAppraisals({ page_size: 100 }),
    ]).then(([dueRes, histRes]) => {
      setData(dueRes.data);
      setAllAppraisals(histRes.data.appraisals || []);
    }).catch(console.error).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="text-center py-12 text-gray-400">Loading...</div>;

  const due = data?.due || [];
  const upcoming = data?.upcoming || [];

  return (
    <div className="space-y-6" data-testid="appraisals-tab">
      <div className="grid grid-cols-3 gap-4">
        <Card className="border-red-200 rounded-2xl"><CardContent className="p-4">
          <div className="flex items-center gap-2 mb-1"><WarningCircle size={16} className="text-red-600" /><span className="text-xs text-gray-500">Due Now</span></div>
          <p className="text-2xl font-bold text-red-600">{due.length}</p>
        </CardContent></Card>
        <Card className="border-amber-200 rounded-2xl"><CardContent className="p-4">
          <div className="flex items-center gap-2 mb-1"><Clock size={16} className="text-amber-600" /><span className="text-xs text-gray-500">Upcoming (90 days)</span></div>
          <p className="text-2xl font-bold text-amber-600">{upcoming.length}</p>
        </CardContent></Card>
        <Card className="border-green-200 rounded-2xl"><CardContent className="p-4">
          <div className="flex items-center gap-2 mb-1"><CheckCircle size={16} className="text-green-600" /><span className="text-xs text-gray-500">Completed</span></div>
          <p className="text-2xl font-bold text-green-600">{allAppraisals.length}</p>
        </CardContent></Card>
      </div>

      <div className="flex gap-2">
        <Button variant={view === 'due' ? 'default' : 'outline'} size="sm" onClick={() => setView('due')}
          className={`rounded-xl ${view === 'due' ? 'bg-[#0B1B3D]' : ''}`}>Overdue & Upcoming</Button>
        <Button variant={view === 'history' ? 'default' : 'outline'} size="sm" onClick={() => setView('history')}
          className={`rounded-xl ${view === 'history' ? 'bg-[#0B1B3D]' : ''}`}>History</Button>
      </div>

      {view === 'due' ? (
        <div className="space-y-2">
          {due.length === 0 && upcoming.length === 0 ? (
            <p className="text-center text-gray-400 py-8">All appraisals are up to date!</p>
          ) : (
            <>
              {due.length > 0 && <h4 className="text-sm font-semibold text-red-600">Overdue ({due.length})</h4>}
              {due.map(emp => (
                <Card key={emp.employee_number} className="border border-red-100 rounded-xl" data-testid={`appraisal-due-${emp.employee_number}`}>
                  <CardContent className="p-3 flex items-center justify-between">
                    <div>
                      <p className="font-medium text-sm text-[#0B1B3D]">{emp.display_name || emp.full_name}</p>
                      <p className="text-xs text-gray-500">{emp.employee_number} — {emp.department} — {emp.location}</p>
                      <p className="text-xs text-gray-400">Last: {emp.last_appraisal_date || 'Never'} | Joined: {emp.date_joined || '—'}</p>
                    </div>
                    <Badge className="bg-red-100 text-red-700 border-red-200">Overdue</Badge>
                  </CardContent>
                </Card>
              ))}
              {upcoming.length > 0 && <h4 className="text-sm font-semibold text-amber-600 mt-4">Upcoming ({upcoming.length})</h4>}
              {upcoming.map(emp => (
                <Card key={emp.employee_number} className="border border-amber-100 rounded-xl">
                  <CardContent className="p-3 flex items-center justify-between">
                    <div>
                      <p className="font-medium text-sm text-[#0B1B3D]">{emp.display_name || emp.full_name}</p>
                      <p className="text-xs text-gray-500">{emp.employee_number} — {emp.department}</p>
                      <p className="text-xs text-gray-400">Due in {emp.days_until_due} days</p>
                    </div>
                    <Badge className="bg-amber-100 text-amber-700 border-amber-200">In {emp.days_until_due}d</Badge>
                  </CardContent>
                </Card>
              ))}
            </>
          )}
        </div>
      ) : (
        <div className="space-y-2">
          {allAppraisals.length === 0 ? (
            <p className="text-center text-gray-400 py-8">No appraisals recorded yet</p>
          ) : allAppraisals.map(a => (
            <Card key={a.id} className="border rounded-xl" data-testid={`appraisal-history-${a.id}`}>
              <CardContent className="p-3">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="font-medium text-sm text-[#0B1B3D]">{a.employee_name}</p>
                    <p className="text-xs text-gray-500">{a.employee_number} — {a.department}</p>
                  </div>
                  <div className="text-right">
                    <Badge variant="outline" className="text-xs">{a.rating || '—'}</Badge>
                    <p className="text-xs text-gray-400 mt-1">{a.appraisal_date}</p>
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-2 mt-2 text-xs">
                  <div className="bg-gray-50 rounded-lg p-2"><span className="text-gray-400">Old CTC</span><p className="font-medium">₹{fmt(a.old_ctc_annual)}</p></div>
                  <div className="bg-green-50 rounded-lg p-2"><span className="text-gray-400">New CTC</span><p className="font-medium text-green-700">₹{fmt(a.new_ctc_annual)}</p></div>
                  <div className="bg-blue-50 rounded-lg p-2"><span className="text-gray-400">Increment</span><p className="font-medium text-blue-700">{a.increment_percent || 0}%</p></div>
                </div>
                {a.remarks && <p className="text-xs text-gray-400 mt-2">{a.remarks}</p>}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
